import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Product, StoreProduct } from '@nucleus-commerce/api-client/admin';

import { StoreProductAuctionPanel } from '@/features/auctions';
import { ProductCollectionsCard, useProductCollectionsDraft } from '@/features/collections';
import {
  ProductMediaSection,
  ProductScopeProvider,
  ScopeChip,
  useProduct,
  useProductGalleryDraft,
  useSharedProductSections,
  type ProductScope,
  type SharedProductSections,
} from '@/features/products';
import { useStore } from '@/shared/store/store-context';

import { displayTitle } from '../api/mappers';
import { ScheduleOwnerBanner } from '../components/schedule-owner-banner';
import { StoreProductForm } from '../components/store-product-form';
import { StoreProductHeader } from '../components/store-product-header';
import { StoreInsightsCard, StoreOtherStoresCard } from '../components/store-rail-cards';
import { StoreVariantsSection } from '../components/store-variants-section';
import { storeCatalogKeys } from '../hooks/store-catalog-keys';
import { useOtherStores } from '../hooks/use-other-stores';
import { useProductStoreCount } from '../hooks/use-product-store-count';
import { useStoreProduct } from '../hooks/use-store-product';

const ROUTE = '/_authed/_active/s/$storeId/catalog/$storeProductId' as const;

/** The master product's cards for this page; see `useSharedProductSections`. */
function WithSharedSections({
  product,
  scope,
  onSaved,
  children,
}: {
  product: Product;
  scope: ProductScope;
  onSaved: () => void;
  children: (shared: SharedProductSections) => ReactNode;
}) {
  return children(useSharedProductSections({ product, scope, onSaved }));
}

/** What the two chips mean, once, under the header. */
function ScopeLegend({ storeName, storeCount }: { storeName: string; storeCount: number }) {
  const { t } = useTranslation('storeCatalog');
  return (
    <p className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px]">
      <ScopeChip scope="store" />
      <span>{t('storePage.legend.store', { store: storeName })}</span>
      <ScopeChip scope="shared" />
      <span>{t('storePage.legend.shared', { count: storeCount })}</span>
    </p>
  );
}

function StoreProductEditor() {
  const store = useStore();
  const { storeProductId } = useParams({ from: ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: row } = useStoreProduct(store.id, storeProductId);
  if (!row) return null;
  // Previous and next stay on this route: nothing of one product may carry over to the other.
  return <StoreProductEditorBody key={row.id} row={row} />;
}

function StoreProductEditorBody({ row }: { row: StoreProduct }) {
  const store = useStore();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  // A save of the master product changes what this row and the store's list carry of it.
  const refreshMaster = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: storeCatalogKeys.detail(store.id, row.id),
      exact: true,
    });
    void queryClient.invalidateQueries({ queryKey: storeCatalogKeys.lists(store.id) });
  }, [queryClient, store.id, row.id]);
  const gallery = useProductGalleryDraft(row.productId, row.product.featuredMediaId, {
    onSaved: refreshMaster,
  });
  const collections = useProductCollectionsDraft(store.id, row.productId);
  // Prefetched by the route loader with the row, so the shared cards render with the page.
  const { data: product } = useProduct(row.productId);
  const others = useOtherStores(store.id, row.productId);
  // At least this one, while the other stores' answers are on their way.
  const storeCount = Math.max(useProductStoreCount(row.productId), 1);
  // The rotation that owns this store product; its runs set the status (D-181).
  const owner = row.schedule;
  const scope: ProductScope = {
    readOnly: !can('products.write'),
    shared: true,
    productId: row.productId,
    storeName: store.name,
    storeCount,
  };

  const page = (shared?: SharedProductSections) => (
    <ProductScopeProvider value={scope}>
      <PageContent>
        <StoreProductHeader
          storeId={store.id}
          row={row}
          others={others}
          productTypeName={shared?.productTypeName ?? null}
        />
        <div className="flex flex-col gap-6">
          <StoreProductAuctionPanel storeProductId={row.id} title={displayTitle(row)} />
          {owner ? <ScheduleOwnerBanner schedule={owner} /> : null}
          <ScopeLegend storeName={store.name} storeCount={storeCount} />
          <StoreProductForm
            storeId={store.id}
            row={row}
            owner={owner}
            shared={shared}
            media={(notice) => <ProductMediaSection gallery={gallery} notice={notice} />}
            variants={
              product ? (
                <StoreVariantsSection
                  storeId={store.id}
                  storeProductId={row.id}
                  product={product}
                  currency={store.defaultCurrency}
                />
              ) : null
            }
            collections={
              <ProductCollectionsCard
                draft={collections}
                storeName={store.name}
                productId={row.productId}
                badge={<ScopeChip scope="store" />}
                tour="store-catalog.product.collections"
              />
            }
            railEnd={
              <>
                <StoreOtherStoresCard others={others} masterStatus={row.product.status} />
                <Can permission="reports.read">
                  <StoreInsightsCard storeId={store.id} productId={row.productId} />
                </Can>
              </>
            }
          />
        </div>
      </PageContent>
    </ProductScopeProvider>
  );

  return product ? (
    <WithSharedSections product={product} scope={scope} onSaved={refreshMaster}>
      {page}
    </WithSharedSections>
  ) : (
    page()
  );
}

export function StoreProductPage() {
  return (
    <FeatureErrorBoundary name="storeCatalog.edit">
      <StoreProductEditor />
    </FeatureErrorBoundary>
  );
}
